import fileUpload from '../../Vendor/inv-file-upload/fileUpload';
import anonymousRepositoryHelper from './anonymousRepositoryHelper';
import UploadStatus from '../Types/enums/UploadStatus';

function UploadRepository() {

  var self = this;
  var baseResourceUrl = '/api/upload/';

  /**
   * Gets a list of uploads for given response public reference parameters. The uploads are retrieved from the server and either
   * returned directly or used to lookup active / pending uploads from the upload manager which are
   * returned instead.
   * @param {string} responsePublicReference
   * @return {Promise<UploadModel[]>}
   */
  self.getUploadsByResponsePublicReference = function (responsePublicReference) {

    var resourceUrl = baseResourceUrl + 'response/' + responsePublicReference;

    return new Promise(function (resolve, reject) {

      anonymousRepositoryHelper.ajaxGet(resourceUrl)
        .then(function (serverUploads) {
          var uploads = createUploadModels(serverUploads);
          resolve(uploads);
        })
        .catch(function (jqXhr) {
          reject(jqXhr);
        });
    });
  };

  /**
   * Adds one or more files for upload. The newly created uploads are returned immediately. Asynchronously,
   * server calls are made to create the new uploads / generate upload tokens and then the upload engine
   * used to start the uploads.
   * @param {object} fileList An array of native browser File objects to be uploaded
   * @param {string} responsePublicReference
   * @param {Function} addUploadsFailedCallback Callback to be called with an error on failure
   * @return {Array}
   */
  self.addUploads = function (fileList, responsePublicReference, addUploadsFailedCallback) {

    var i;
    var file;
    var upload;
    var files = [];
    var uploads = [];
    var serverUpload = null;

    for (i = 0; i < fileList.length; i++) {
      file = fileList[i];
      files.push(file);
      upload = new fileUpload.UploadModel(serverUpload, file);
      subscribeToCompleteUploadStatusName(upload);
      uploads.push(upload);
    }

    // Asynchronously create each upload on the server / generate upload token then start the uploads
    createPromiseToAddUploadsInOrder(files, responsePublicReference)
      .then(function (serverUploads) {
        startUploads(uploads, serverUploads);
      })
      .catch(function (e) {

        var upload;

        for (i = 0; i < uploads.length; i++) {
          upload = uploads[i];
          upload.setUploadFailedAuthorisation();
        }

        addUploadsFailedCallback(e);
      });

    return uploads;
  };

  /**
   * Takes an array of uploads and updates each via the server if the upload has been changed
   * @param {UploadModel[]} uploads
   * @return {Promise}
   */
  self.updateUploads = function (uploads) {
    var resourceUrl = baseResourceUrl + 'UpdateUploads';
    var updateUploadPromises = [];
    var uploadData = [];

    var i;
    for (i = 0; i < uploads.length; i++) {
      if (!uploads[i].commentHasChanged()) {
        continue;
      }

      var singleUploadData = {
        uploadPublicReference: uploads[i].publicReference,
        comments: uploads[i].comments()
      };
      uploadData.push(singleUploadData);
    }

    if (uploadData.length > 0) {
      updateUploadPromises.push(anonymousRepositoryHelper.ajaxPost(resourceUrl, uploadData));
    }

    return Promise.all(updateUploadPromises);
  };

  function createPromiseToAddUploadsInOrder(files, responsePublicReference) {

    var serverUploads = [];
    var initialResolvedPromise = Promise.resolve(null);

    // ----------------------------------------------------------------------------------------------------
    // For each upload to be posted to the server, chain each Ajax request off the previous Ajax promise so
    // that each request is made in series one after the other. The initial promise is automatically
    // resolved to allow the first Ajax request call to be made immediately. Once each Ajax request
    // promise is resolved, add each serverUpload to the serverUploads array. This is done so that the
    // order of the uploads initially rendered in the table is that same as the order they are  created in
    // the database so the table will always display as expected sorted by created date.
    // ----------------------------------------------------------------------------------------------------
    var promise = files.reduce(function (previousPromise, file) {

      return previousPromise.then(function () {

        return postNewUploadToServer(file, responsePublicReference)
          .then(function (serverUpload) {
            serverUploads.push(serverUpload);
          });
      });

    }, initialResolvedPromise); // Initial resolved promise is the starting previousPromise

    // ----------------------------------------------------------------------------------------------------
    // Once all the Ajax requests have been made in series, return the serverUploads if all Ajax requests
    // were successful or the error details if not.
    // ----------------------------------------------------------------------------------------------------
    return new Promise(function (resolve, reject) {
      promise.then(function () {
        resolve(serverUploads);
      })
        .catch(function (e) {
          reject(e);
        });
    });
  }

  /**
   * Start each upload after all entities created / upload tokens generated on the server
   * @param {UploadModel[]} uploads
   * @param {*} serverUploads Array of server models including upload token property
   */
  function startUploads(uploads, serverUploads) {
    serverUploads.forEach(serverUpload => {
      const upload = uploads.find(x => x.fileName === serverUpload.fileName);
      upload.updateAfterServerModelCreated(serverUpload);
      fileUpload.uploadManager.addUpload(upload);
    });
  }

  function postNewUploadToServer(file, responsePublicReference) {

    var uploadData = {
      responsePublicReference: responsePublicReference,
      fileName: file.name,
      fileSize: file.size,
      // lastModifiedDate is actually depreciated and looks like it’s not supported on some browsers (including FireFox)
      // The method lastModified returns the date as number of milliseconds since the Unix epoch (January 1, 1970 at midnight).
      // The method lastModifiedDate returns a Date object.
      lastWriteDate: (file.lastModified ? new Date(file.lastModified) : file.lastModifiedDate).toISOString()
      // Posting only standard file data
    };

    return anonymousRepositoryHelper.ajaxPost(baseResourceUrl, uploadData);
  }

  /**
   * Deletes an upload by making a call to the service and cancels an active or pending upload
   * @param {UploadModel} upload
   * @return {Promise}
   */
  self.deleteUpload = function (upload) {

    var resourceUrl = baseResourceUrl + upload.publicReference;

    if (upload.statusName() === UploadStatus.pending ||
        upload.statusName() === UploadStatus.uploading) {
      fileUpload.uploadManager.cancelUpload(upload);
    }

    // If the upload failed authorization then there is no upload to delete so return with a resolved
    // promise. For uploads that are authorizing the user can't remove the upload.
    if (upload.statusName() === UploadStatus.failedAuthorisation) {
      return Promise.resolve(null);
    }

    return anonymousRepositoryHelper.ajaxDelete(resourceUrl);
  };

  self.uploadCompleteCallback = function (publicReference) {
    var resourceUrl = baseResourceUrl + publicReference;
    return anonymousRepositoryHelper.ajaxPut(resourceUrl);
  };

  function createUploadModels(serverUploads) {

    var serverUpload;
    var uploads = [];

    for (var i = 0; i < serverUploads.length; i++) {
      serverUpload = serverUploads[i];
      var upload = getOrCreateUploadModel(serverUpload);
      uploads.push(upload);
    }

    return uploads;
  }

  function getOrCreateUploadModel(serverUpload) {
    var upload = fileUpload.uploadManager.getUpload(serverUpload.fileName, serverUpload.parentId);
    var file = {};
    var uploadModel = new fileUpload.UploadModel(serverUpload, file);

    return upload ? upload : uploadModel;
  }

  function subscribeToCompleteUploadStatusName(upload) {
    upload.statusName.subscribe(function (value) {
      if (value === UploadStatus.uploaded) {
        self.uploadCompleteCallback(upload.publicReference);
      }
    });
  }
}

export default new UploadRepository();
